import {
  DataStatus,
  Form,
  FormField,
  type FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, Heading, Text, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {
  CreateTeamRequestBody,
  useCreateTeamMutation,
  useGetTeamQuery,
  useUpdateTeamMutation,
} from '@dms/api/customer';
import {i18n} from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {handleApiError, useBranches, useGetUserOptions} from '@dms/shared';

import {ApiError, composePath, useNavigate} from 'shared/web';

import {TeamFormSchema, type TeamFormValues} from './schemas/TeamFormSchema';

const ASSIGNMENT_STRATEGY_OPTIONS = [
  {
    value: 'assign_to_lead',
    label: i18n.t('entity.team.labels.assignToLead'),
  },
  {
    value: 'least_recently_assigned',
    label: i18n.t('entity.team.labels.leastRecentlyAssigned'),
  },
];

const DEFAULT_VALUES: Partial<CreateTeamRequestBody> = {
  active: true,
  branches: [],
  leadId: null,
  deputies: [],
  assignmentStrategy: 'least_recently_assigned',
  timeToAcceptAssignment: null,
  maximumEscalations: null,
};

export interface TeamFormProps {
  teamId?: string;
  isReadOnlyForm?: boolean;
}

export function TeamForm(props: TeamFormProps) {
  const navigate = useNavigate();
  const branchesQuery = useBranches();

  const {userOptions, isLoading: isUsersLoading} = useGetUserOptions();
  const {
    data: teamData,
    isLoading: isTeamLoading,
    isError: isTeamError,
  } = useGetTeamQuery({id: props.teamId!}, {skip: !props.teamId});

  const [createTeam] = useCreateTeamMutation();

  const [updateTeam] = useUpdateTeamMutation();

  const isEditing = Boolean(props.teamId);

  const handleSubmit: FormSubmitHandler<TeamFormValues> = (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (isEditing) {
      return updateTeam({
        id: props.teamId!,
        updateTeamRequestBody: {...values, active: !!values.active},
      })
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
          if (!blockerContext?.wasSubmittedFromBlocker) {
            navigate(settingsRoutes.teamManagement);
          }
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    return createTeam({createTeamRequestBody: values})
      .unwrap()
      .then((result) => {
        showNotification.success(i18n.t('entity.team.notifications.successfullyCreated'));
        if (!blockerContext?.wasSubmittedFromBlocker) {
          navigate(
            composePath(settingsRoutes.teamManagementDetail, {
              params: {id: result.id},
              queryParams: {tab: 'members'},
            })
          );
        }
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const baseTestId = isEditing
    ? testIds.settings.teamManagementDetail
    : testIds.settings.teamManagementAddTeam;

  return (
    <DataStatus isLoading={isTeamLoading} isError={isTeamError} minHeight={60}>
      <SettingsSection>
        <Form<TeamFormValues>
          experimentalZodSchema={TeamFormSchema}
          defaultValues={teamData ?? DEFAULT_VALUES}
          onSubmit={handleSubmit}
          data-testid={baseTestId('form')}
          isDisabled={props.isReadOnlyForm}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi, navigateWithoutBlocker) => {
            const isTimeToAcceptAssignmentRequired = isNotNil(formApi.watch('maximumEscalations'));
            const isMaximumEscalationsRequired = isNotNil(formApi.watch('timeToAcceptAssignment'));

            return (
              <VStack spacing={6}>
                <FormField
                  type="switch"
                  control={control}
                  name="active"
                  label={i18n.t('entity.team.labels.setTeamAsActive')}
                  helperText={i18n.t('entity.team.labels.activeTeamDescription')}
                  data-testid={baseTestId('active')}
                />

                <Separator spacing={1} />

                <Grid columns={2}>
                  <GridItem span={2}>
                    <FormField
                      control={control}
                      name="name"
                      type="text"
                      label={i18n.t('entity.team.labels.teamName')}
                      data-testid={baseTestId('name')}
                      isRequired
                    />
                  </GridItem>

                  <GridItem>
                    <FormField
                      type="multiChoice"
                      control={control}
                      name="branches"
                      label={i18n.t('entity.branch.labels.branch')}
                      tooltip={i18n.t('entity.team.labels.branchTooltip')}
                      options={branchesQuery.branchOptions}
                      data-testid={baseTestId('branches')}
                    />
                  </GridItem>
                </Grid>

                <VStack spacing={1}>
                  <Grid columns={2}>
                    <GridItem>
                      <FormField
                        control={control}
                        name="leadId"
                        type="choice"
                        label={i18n.t('entity.team.labels.teamLead')}
                        options={userOptions}
                        isDisabled={isUsersLoading}
                        data-testid={baseTestId('leadId')}
                      />
                    </GridItem>

                    <GridItem>
                      <FormField
                        control={control}
                        name="deputies"
                        type="multiChoice"
                        label={i18n.t('entity.team.labels.deputies')}
                        options={userOptions}
                        isDisabled={isUsersLoading}
                        data-testid={baseTestId('deputies')}
                      />
                    </GridItem>
                  </Grid>

                  <Text size="xSmall" color="tertiary">
                    {i18n.t('entity.team.labels.leadAndDeputiesHelperText')}
                  </Text>
                </VStack>

                <Grid columns={2}>
                  <GridItem>
                    <FormField
                      control={control}
                      name="assignmentStrategy"
                      type="choice"
                      label={i18n.t('entity.team.labels.assignmentStrategy')}
                      tooltip={i18n.t('entity.team.labels.assignmentStrategyTooltip')}
                      options={ASSIGNMENT_STRATEGY_OPTIONS}
                      data-testid={baseTestId('assignmentStrategy')}
                      isRequired
                    />
                  </GridItem>
                </Grid>

                <Separator spacing={1} />
                <Heading size={3}>{i18n.t('entity.team.labels.escalation')}</Heading>
                <Text size="small" color="secondary">
                  {i18n.t('entity.team.labels.escalationDescription')}
                </Text>
                <Separator spacing={1} />
                <Grid columns={2}>
                  <GridItem>
                    <FormField
                      control={control}
                      name="timeToAcceptAssignment"
                      type="integer"
                      suffix={i18n.t('general.metric.min')}
                      label={i18n.t('entity.team.labels.timeToAcceptAssignment')}
                      data-testid={baseTestId('timeToAcceptAssignment')}
                      isRequired={isTimeToAcceptAssignmentRequired}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      control={control}
                      name="maximumEscalations"
                      type="integer"
                      label={i18n.t('entity.team.labels.maximumEscalations')}
                      isStepperVisible
                      minStepperValue={1}
                      data-testid={baseTestId('maximumEscalations')}
                      isRequired={isMaximumEscalationsRequired}
                    />
                  </GridItem>
                </Grid>
                <SettingsFooter
                  actions={[
                    {
                      type: 'button',
                      title: isEditing
                        ? i18n.t('general.actions.discardChanges')
                        : i18n.t('general.actions.discard'),
                      variant: 'secondary',
                      onClick: () => navigateWithoutBlocker(settingsRoutes.teamManagement),
                      'data-testid': baseTestId('discard'),
                    },
                    {
                      type: 'form-button',
                      control,
                      title: isEditing
                        ? i18n.t('general.actions.saveChanges')
                        : i18n.t('general.actions.create'),
                      buttonType: 'submit',
                      'data-testid': baseTestId('submit'),
                    },
                  ]}
                />
              </VStack>
            );
          }}
        </Form>
      </SettingsSection>
    </DataStatus>
  );
}
